import { useEffect, useState } from "react";
import {
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  collection,
  addDoc,
  serverTimestamp,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

function Profile() {
  const { user, profile, loading } = useAuth();

  const [error, setError] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    fullName: "",
    startupName: "",
    description: "",
    website: "",
  });

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");

  const [shareEmail, setShareEmail] = useState("");
  const [shareStatus, setShareStatus] = useState("");
  const [sharing, setSharing] = useState(false);

  const [sharedWith, setSharedWith] = useState([]);
  const [loadingShares, setLoadingShares] = useState(true);

  const [publicShare, setPublicShare] = useState(null);
  const [shareExpiryDays, setShareExpiryDays] = useState(7);
  const [publicShareStatus, setPublicShareStatus] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  const [openSection, setOpenSection] = useState("profile");

  /* ================= INIT PROFILE DATA ================= */
  useEffect(() => {
    if (!profile) return;
    setEditData({
      fullName: profile.fullName || "",
      startupName: profile.startupName || "",
      description: profile.description || "",
      website: profile.website || "",
    });
  }, [profile]);

  /* ================= LOAD SHARES ================= */
  useEffect(() => {
    if (!user?.uid) return;

    const loadShares = async () => {
      try {
        setLoadingShares(true);
        const q = query(
          collection(db, "shares"),
          where("founderId", "==", user.uid)
        );
        const snap = await getDocs(q);
        setSharedWith(
          snap.docs.map((d) => ({
            shareId: d.id,
            evaluatorEmail: d.data().evaluatorEmail,
            sharedAt: d.data().sharedAt,
          }))
        );
      } catch (err) {
        console.error("Failed to load shares:", err);
      } finally {
        setLoadingShares(false);
      }
    };

    loadShares();
  }, [user?.uid]);

  /* ================= LOAD EXISTING PUBLIC SHARE ================= */
  useEffect(() => {
    if (!user?.uid) return;

    const loadPublicShare = async () => {
      try {
        const q = query(
          collection(db, "publicProfileShares"),
          where("founderId", "==", user.uid),
          where("revoked", "==", false),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          setPublicShare({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (err) {
        console.error("Failed to load public share link:", err);
      }
    };

    loadPublicShare();
  }, [user?.uid]);

  /* ================= SAVE PROFILE EDITS ================= */
  const saveProfileEdits = async () => {
    try {
      await updateDoc(doc(db, "users", user.uid), { ...editData });
      setIsEditing(false);
    } catch {
      alert("Failed to save profile.");
    }
  };

  /* ================= LOGO UPLOAD ================= */
  const handleLogoUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload a valid image file.");
      return;
    }

    const MAX_SIZE = 1 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setLogoError("Startup logo must be smaller than 1 MB.");
      return;
    }

    try {
      setLogoError("");
      setLogoUploading(true);

      const storageRef = ref(storage, `startup-logos/${user.uid}/logo`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "users", user.uid), {
        startupLogoUrl: url,
      });
    } catch {
      setLogoError("Logo upload failed.");
    } finally {
      setLogoUploading(false);
    }
  };

  /* ================= SHARE WITH EVALUATOR ================= */
  const handleShare = async () => {
    if (!shareEmail) {
      setShareStatus("Please enter an evaluator email.");
      return;
    }

    try {
      setSharing(true);
      setShareStatus("");

      const q = query(
        collection(db, "users"),
        where("email", "==", shareEmail),
        limit(1)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setShareStatus("No evaluator found with this email.");
        return;
      }

      const evaluatorDoc = snap.docs[0];
      const evaluatorData = evaluatorDoc.data();

      if (evaluatorData.persona !== "evaluator") {
        setShareStatus("This user is not an evaluator.");
        return;
      }

      const newShareRef = await addDoc(collection(db, "shares"), {
        founderId: user.uid,
        evaluatorId: evaluatorDoc.id,
        evaluatorEmail: evaluatorData.email,
        sharedAt: serverTimestamp(),
        status: "active",
      });

      setSharedWith((prev) => [
        ...prev,
        {
          shareId: newShareRef.id,
          evaluatorEmail: evaluatorData.email,
          sharedAt: { toDate: () => new Date() },
        },
      ]);

      setShareEmail("");
      setShareStatus("Profile shared successfully.");
    } catch (err) {
      console.error(err);
      setShareStatus("Failed to share profile.");
    } finally {
      setSharing(false);
    }
  };

  /* ================= REVOKE SHARE ================= */
  const revokeShare = async (shareId) => {
    if (!window.confirm("Revoke access for this evaluator?")) return;

    try {
      await deleteDoc(doc(db, "shares", shareId));
      setSharedWith((prev) => prev.filter((s) => s.shareId !== shareId));
    } catch {
      alert("Failed to revoke access.");
    }
  };
  /* ================= RENDER STATES ================= */
  if (loading) return <p>Loading profile...</p>;
  if (!profile) return <p>No profile data available.</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  const verificationStatus = profile.verification?.status || "unverified";
  const industries = profile.industries || [];

  return (
    <div>
      <h1
        title="This is your startup’s primary profile visible to evaluators you share with"
      >
        Profile{" "}
        {verificationStatus === "verified" && (
          <span style={{ marginLeft: 8, color: "#2563eb" }} title="This profile has been verified by Prufyy">
            ✔ Verified by Prufyy
          </span>
        )}
      </h1>

      {/* ================= PROFILE + LOGO ================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 32,
          marginTop: 32,
          alignItems: "start",
        }}
      >
        {/* ===== PROFILE CARD ===== */}
        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
            transition: "all 0.3s ease",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          }}
        >
          {/* Collapsible Header */}
          <div
            onClick={() =>
              setOpenSection(openSection === "profile" ? null : "profile")
            }
            title="View and edit your founder and startup information"
            style={{
              padding: 20,
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background:
                "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))",
            }}
          >
            <h3 style={{ margin: 0 }}>Profile Information</h3>
            <span style={{ fontSize: 18 }}>
              {openSection === "profile" ? "−" : "+"}
            </span>
          </div>

          {/* Collapsible Content */}
          {openSection === "profile" && (
            <div
              style={{
                padding: 24,
                maxHeight: 420,
                overflowY: "auto",
              }}
            >
              {isEditing ? (
                <>
                  <input
                    value={editData.fullName}
                    onChange={(e) =>
                      setEditData({ ...editData, fullName: e.target.value })
                    }
                    placeholder="Founder name"
                    title="Your full name as the primary founder"
                    style={{ width: "100%", marginBottom: 10 }}
                  />
                  <input
                    value={editData.startupName}
                    onChange={(e) =>
                      setEditData({ ...editData, startupName: e.target.value })
                    }
                    placeholder="Startup name"
                    title="The official name of your startup"
                    style={{ width: "100%", marginBottom: 10 }}
                  />
                  <input
                    value={editData.website}
                    onChange={(e) =>
                      setEditData({ ...editData, website: e.target.value })
                    }
                    placeholder="Website"
                    title="Public website evaluators can visit for context"
                    style={{ width: "100%", marginBottom: 10 }}
                  />
                  <textarea
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    placeholder="Startup description"
                    title="A short, factual description — avoid marketing language"
                    rows={4}
                    style={{ width: "100%", marginBottom: 14 }}
                  />

                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      className="btn-primary"
                      onClick={saveProfileEdits}
                      title="Save your profile changes"
                    >
                      Save Changes
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setIsEditing(false)}
                      title="Discard changes and exit edit mode"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Founder:</strong> {profile.fullName}</p>
                  <p><strong>Email:</strong> {profile.email}</p>
                  <p><strong>Your role:</strong> {profile.founderRole}</p>
                  <p><strong>Startup:</strong> {profile.startupName}</p>
                  <p><strong>Description:</strong> {profile.description}</p>

                  <div style={{ marginTop: 10 }}>
                    <strong title="Industries help evaluators quickly contextualize your startup">
                      Industries:
                    </strong>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 6,
                      }}
                    >
                      {industries.map((ind) => (
                        <span
                          key={ind}
                          title={`Industry: ${ind}`}
                          style={{
                            padding: "5px 12px",
                            background:
                              "linear-gradient(135deg,#6366f1,#8b5cf6)",
                            color: "#fff",
                            borderRadius: 999,
                            fontSize: 12,
                            boxShadow:
                              "0 3px 8px rgba(99,102,241,0.35)",
                          }}
                        >
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn-secondary"
                    onClick={() => setIsEditing(true)}
                    title="Edit your profile details"
                    style={{ marginTop: 14 }}
                  >
                    Edit Profile
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ===== LOGO CARD ===== */}
        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
            textAlign: "center",
            transition: "all 0.3s ease",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          }}
        >
          <div
            onClick={() =>
              setOpenSection(openSection === "logo" ? null : "logo")
            }
            title="Upload a logo to visually identify your startup"
            style={{
              padding: 20,
              cursor: "pointer",
              background:
                "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.08))",
            }}
          >
            <h3 style={{ margin: 0 }}>Startup Logo</h3>
          </div>

          {openSection === "logo" && (
            <div style={{ padding: 24 }}>
              {profile.startupLogoUrl && (
                <img
                  src={profile.startupLogoUrl}
                  alt="Startup logo"
                  title="Current startup logo"
                  style={{
                    maxHeight: 150,
                    maxWidth: "100%",
                    marginBottom: 16,
                    objectFit: "contain",
                  }}
                />
              )}

              <input
                type="file"
                accept="image/*"
                title="Upload a PNG or JPG logo under 1 MB"
                onChange={(e) => handleLogoUpload(e.target.files[0])}
              />

              {logoUploading && (
                <p style={{ marginTop: 8 }}>Uploading logo…</p>
              )}
              {logoError && (
                <p style={{ color: "red", marginTop: 8 }}>{logoError}</p>
              )}
            </div>
          )}
        </div>
      </div>
      {/* ================= SHARE WITH EVALUATOR ================= */}
      <div
        className="card"
        style={{
          padding: 0,
          marginTop: 36,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
        }}
      >
        <div
          onClick={() =>
            setOpenSection(openSection === "share" ? null : "share")
          }
          title="Share your profile with a specific evaluator by email"
          style={{
            padding: 20,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>Share with Evaluator</h3>
          <span>{openSection === "share" ? "−" : "+"}</span>
        </div>

        {openSection === "share" && (
          <div style={{ padding: 24 }}>
            <input
              type="email"
              placeholder="Enter evaluator email"
              value={shareEmail}
              title="Only registered evaluators can be invited"
              onChange={(e) => setShareEmail(e.target.value)}
              style={{ maxWidth: 360, width: "100%" }}
            />

            <div style={{ marginTop: 14 }}>
              <button
                className="btn-primary"
                onClick={handleShare}
                disabled={sharing}
                title="Grant this evaluator access to your startup profile"
              >
                {sharing ? "Sharing..." : "Share Access"}
              </button>
            </div>

            {shareStatus && (
              <p
                style={{ marginTop: 12, fontSize: 14 }}
                title="Share status message"
              >
                {shareStatus}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ================= SHARED WITH LIST ================= */}
      <div
        className="card"
        style={{
          padding: 0,
          marginTop: 24,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
        }}
      >
        <div
          onClick={() =>
            setOpenSection(openSection === "shared" ? null : "shared")
          }
          title="View and manage evaluators who currently have access"
          style={{
            padding: 20,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(236,72,153,0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>Shared With Evaluators</h3>
          <span>{openSection === "shared" ? "−" : "+"}</span>
        </div>

        {openSection === "shared" && (
          <div
            style={{
              padding: 20,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {!loadingShares && sharedWith.length === 0 && (
              <p style={{ color: "#64748b" }}>
                Not shared with any evaluators yet.
              </p>
            )}

            {!loadingShares &&
              sharedWith.map((s) => (
                <div
                  key={s.shareId}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <div>
                    <div
                      style={{ fontWeight: 500 }}
                      title="Evaluator email address"
                    >
                      {s.evaluatorEmail}
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#64748b" }}
                      title="Date when access was granted"
                    >
                      Shared on{" "}
                      {s.sharedAt?.toDate
                        ? s.sharedAt.toDate().toLocaleDateString()
                        : ""}
                    </div>
                  </div>

                  <button
                    className="btn-secondary"
                    onClick={() => revokeShare(s.shareId)}
                    title="Revoke this evaluator’s access immediately"
                  >
                    Revoke
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* ================= PUBLIC PROFILE SHARING ================= */}
      <div
        className="card"
        style={{
          padding: 0,
          marginTop: 36,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
        }}
      >
        <div
          onClick={() =>
            setOpenSection(openSection === "public" ? null : "public")
          }
          title="Create a time-limited public link to share your profile"
          style={{
            padding: 20,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>Public Profile Sharing</h3>
          <span>{openSection === "public" ? "−" : "+"}</span>
        </div>

        {openSection === "public" && (
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <select
                value={shareExpiryDays}
                title="Choose how long the public link remains valid"
                onChange={(e) => setShareExpiryDays(Number(e.target.value))}
              >
                <option value={1}>24 hours</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
              </select>

              <button
                className="btn-primary"
                title="Generate a new public, time-limited profile link"
                onClick={async () => {
                  try {
                    setPublicShareStatus("");

                    if (publicShare) {
                      await updateDoc(
                        doc(db, "publicProfileShares", publicShare.id),
                        { revoked: true }
                      );
                    }

                    const token = Math.random()
                      .toString(36)
                      .substring(2, 12);
                    const expiresAt = new Date(
                      Date.now() +
                        shareExpiryDays * 24 * 60 * 60 * 1000
                    );

                    await setDoc(doc(db, "publicProfileShares", token), {
                      founderId: user.uid,
                      expiresAt,
                      revoked: false,
                      createdAt: serverTimestamp(),
                    });

                    setPublicShare({
                      id: token,
                      expiresAt,
                      revoked: false,
                    });

                    setPublicShareStatus("New public link generated.");
                  } catch {
                    setPublicShareStatus("Failed to generate link.");
                  }
                }}
              >
                Generate Link
              </button>
            </div>

            {publicShare && (
              <div style={{ marginTop: 16 }}>
                <input
                  readOnly
                  title="This link can be shared publicly until it expires"
                  value={`${window.location.origin}/p/share/${publicShare.id}`}
                  style={{ width: "100%" }}
                />

                <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                  <button
                    className="btn-secondary"
                    title="Copy public link to clipboard"
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        `${window.location.origin}/p/share/${publicShare.id}`
                      );
                      setCopyStatus("Link copied!");
                      setTimeout(() => setCopyStatus(""), 2000);
                    }}
                  >
                    Copy Link
                  </button>

                  <button
                    className="btn-secondary"
                    title="Disable this public link immediately"
                    onClick={async () => {
                      try {
                        await updateDoc(
                          doc(db, "publicProfileShares", publicShare.id),
                          { revoked: true }
                        );
                        setPublicShare(null);
                        setPublicShareStatus("Public link revoked.");
                      } catch {
                        setPublicShareStatus("Failed to revoke link.");
                      }
                    }}
                  >
                    Revoke Link
                  </button>
                </div>

                {copyStatus && (
                  <p
                    style={{ marginTop: 8, fontSize: 13, color: "#16a34a" }}
                    title="Clipboard status"
                  >
                    {copyStatus}
                  </p>
                )}
              </div>
            )}

            {publicShareStatus && (
              <p style={{ marginTop: 14, fontSize: 14 }}>
                {publicShareStatus}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
